/**
 * @file localization-mixin.ts
 *
 * Owns the LocalizationController instance and exposes language-related
 * helpers. Instantiates the controller in the constructor (so lang-file
 * fetching starts before the element connects) and attaches event listeners
 * in connectedCallback.
 *
 * Exposes: _loc, showDebug, updateDebugPanel, updateLangSubscribers
 */
import { LocalizationController } from '../localization.ts';
import { type Constructor } from './mixin-types-utils.ts';

/** Surface added by LocalizationMixin. Used by nothing currently — kept for
 *  future mixins that need to trigger language updates. */
export interface LocalizationMixinMembers {
  _loc: LocalizationController;
  updateLangSubscribers(): void;
  updateDebugPanel(): void;
  showDebug: boolean;
}

export const LocalizationMixin = <T extends Constructor<HTMLElement>>(Base: T) =>
  class LocalizationMixin extends Base {
    _loc: LocalizationController;

    constructor(...args: any[]) {
      super(...args);
      this._loc = new LocalizationController(this, this.getAttribute('lang') ?? 'en');
      this._loc.fetchLangFiles();
    }

    async connectedCallback() {
      await super.connectedCallback?.();
      this._loc.attachListeners();
    }

    disconnectedCallback() {
      super.disconnectedCallback?.();
    }

    get showDebug(): boolean { return this.hasAttribute('show-debug'); }
    set showDebug(value: boolean) {
      if (value) this.setAttribute('show-debug', ''); else this.removeAttribute('show-debug');
    }

    updateDebugPanel(): void {
      const inspector = this.shadowRoot?.getElementById('inspector');
      if (!inspector) return;
      if (this.showDebug) inspector.setAttribute('show-debug', ''); else inspector.removeAttribute('show-debug');
    }

    updateLangSubscribers(): void { this._loc.updateLangSubscribers(); }
  };
